import { Box, Button, ButtonGroup, Grid, List, ListItem, ListItemText, Modal, Paper, TextField, Typography } from "@material-ui/core";
import { default as React, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { RootState } from "state/reducers/AppReducer";
import { FixMeLater } from "types/FixMeLaterType";
import AlertDialog from "../../../components/alert-dialog/AlertDialog";
import DropdownSelect from "../../../components/dropdown-select/DropdownSelect";
import FileDrop from "../../../components/file-drop/FileDrop";
import { HeaderBlock } from "../../../components/header-block/HeaderBlock";
import HelpCard from "../../../components/help-card/HelpCard";
import ListableCard from "../../../components/listable-card/ListableCard";
import { Loading } from "../../../components/loading/Loading";
import PageContainer from "../../../components/page-container/PageContainer";
import CustomizedSnackbars from "../../../components/snackbar/Snackbar";
import TitledCard from "../../../components/titled-card/TitledCard";
import { getCampaigns } from "../../../state/actions/CampaignActions";
import { createTargetGroup, deleteTargetGroup, getTargetGroup, resetLocalTargetGroup, updateLocalTargetGroup } from "../../../state/actions/TargetGroupActions";
import { getCodedValues } from "../../../state/actions/CodedValueActions";
import { FileType } from "../../../state/constants/FileType";
import { ModelType } from "../../../state/constants/ModelType";
import { createTargetFields, TargetField } from "../../../state/constants/TargetFields";
import { TargetGroupType } from "../../../state/constants/TargetGroupTypes";
import styles from "./TargetsPage.module.scss";
import VariableRuleManager from "../../../containers/variable-rule-manager/VariableRuleManager";

const baseUrl = process.env.REACT_APP_BASE_URL

interface MatchParams {
    targetGroupId: string
}

interface Props extends RouteComponentProps<MatchParams> {
    type: string
}

interface TargetGroupRule {
    Field?: string | null,
    FilterType?: string | null,
    Eq?: boolean | null,
    Matching?: string | null,
    MatchingOneOf?: string[] | null
}

interface TargetGroup {
    id?: string,
    DisplayName: string,
    ModelType: string,
    TokenField: string,
    SalesOrganisationIds: string,
    ContainsAnyToken: string[]
    FilterRules: TargetGroupRule[]
    CallType?: string
}

interface DeleteError {
    error_body: string[],
    error_domain: string,
    error_reason: string
}

function ViewTargetGroupsPage(props: Props) {
    // Selectors
    const currentTargetGroup = useSelector<RootState, FixMeLater>(state => state.TargetGroupReducer.currentTargetGroup)
    const campaigns = useSelector<RootState, FixMeLater>(state => state.CampaignReducer.campaigns)
    const codedValues = useSelector<RootState, FixMeLater>(state => state.CodedValueReducer.codedValues)

    // Utility
    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()

    // Meta-State
    const [loading, setLoading] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")
    const [file, setFile] = useState<File[] | null>(null)
    const [deleteTargetGroupModalOpen, setDeleteTargetGroupModalOpen] = useState(false)
    const [deleteTargetGroupErrorModalOpen, setDeleteTargetGroupErrorModalOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(props?.type !== TargetGroupType.VIEW)

    // Content State
    const [targetGroupId, setTargetGroupId] = useState("")
    const [errorCampaignIds, setErrorCampaignIds] = useState<string[]>([])
    const [fetchCampaigns, setFetchCampaigns] = useState(true)
    const [fetchCodedValues, setFetchCodedValues] = useState(false);

    /*
        Effect for inital load of the page
    */
    useEffect(() => {
        if (props?.type === TargetGroupType.VIEW) {
            setLoading(true)
            setIsEditing(false)
            setTargetGroupId(props.match.params.targetGroupId)
            dispatch(getTargetGroup(props.match.params.targetGroupId, () => {
                setLoading(false)
                setFetchCodedValues(true)
            }))
        } else if (props?.type === TargetGroupType.DISTRIBUTOR) {
            var targetGroup = Object.assign({}, currentTargetGroup);
            targetGroup.ModelType = TargetGroupType.DISTRIBUTOR;
            targetGroup.TokenField = ""
            targetGroup.FilterRules = []
            dispatch(updateLocalTargetGroup(targetGroup, () => {
                setLoading(false)
                setIsEditing(true)
                setFetchCodedValues(true)
            }))
        } else {
            setLoading(false)
            setIsEditing(true)
        }

        return () => {
            dispatch(resetLocalTargetGroup(() => { }))
        }
    }, [])

    /**
     * Effect for fetching campaign data
     */
    useEffect(() => {
        if (fetchCampaigns) {
            dispatch(getCampaigns(() => {
                setFetchCampaigns(false)
            }))
        }
    }, [fetchCampaigns])

    /*
        Effect for fetching coded Values
    */
    useEffect(() => {
        const modelType: string = currentTargetGroup.ModelType.toUpperCase()
        const targetFields: TargetField = createTargetFields()[modelType]
        let possibleRuleOptions: string[] = Object.values(targetFields.RULE).map((field) => field.CodeType)
        if (fetchCodedValues) {
            dispatch(getCodedValues(() => {
                setFetchCodedValues(false)
            }, 1000, possibleRuleOptions, null, null, true))
        }
    }, [fetchCodedValues])

    /**
     * Submit the Target group for update or creation
     * @param creating Boolean for if the target group is new, or is being updated
     */
    function submitTargetGroup(creating = true) {
        // Uses any to cover ambivalent states of

        var succesfulValidation = true;
        let snackMessage: string = "Values Missing: ";
        let invalidOrEmptyRuleNames: any[] = [];
        const targetGroup = Object.assign({}, currentTargetGroup)
        if (targetGroup.FilterRules != null && targetGroup.FilterRules.length > 0) {
            targetGroup.FilterRules.forEach((rule: TargetGroupRule, index: number) => {
                if (rule.MatchingOneOf) {
                    const newMatchingOneOf: string[] = []
                    rule.MatchingOneOf.forEach(item => {
                        if (item != "" && item != "\n" && item.trim().length != 0) {
                            newMatchingOneOf.push(item.trim())
                        }
                    })
                    targetGroup.FilterRules[index].MatchingOneOf = newMatchingOneOf
                } else if (!rule.Matching && !rule.MatchingOneOf && rule.FilterType != "Bool") {
                    // Flag rules that have no value, as they will be removed and not processed.
                    invalidOrEmptyRuleNames.push(rule.Field)
                    succesfulValidation = false;
                }
            })
        }

        // Filter any worthless rules.
        snackMessage = snackMessage + invalidOrEmptyRuleNames.join(', ');

        if (targetGroup.TokenField === "") {
            targetGroup.TokenField = "id"
        }

        if (targetGroup.ContainsAnyToken == null) {
            targetGroup.ContainsAnyToken = []
        }

        if (succesfulValidation) {
            dispatch(createTargetGroup(targetGroup, (success: boolean) => {
                if (success) {
                    if (creating) {
                        props.history.push(props.type === TargetGroupType.DISTRIBUTOR ? "/distributors" : "/target-groups");
                    } else {
                        setIsEditing(false)
                    }
                }
            }))
        } else {
            setSnackbarMessage(snackMessage);
            setSnackbarOpen(true);
        }
    }

    /**
     * Parse the filter csv upload
     * @param files CSV file to be parsed
     */
    function parseFilterUpload(files: File[]) {
        const file = files[0]
        if (file != null) {
            const fileReader = new FileReader()

            const eventListener = (event: ProgressEvent<FileReader>) => {

                let csvdata: FixMeLater = event?.target?.result;

                var finalList: string[] = []

                csvdata?.split(",").forEach((value: string) => {
                    if (value != "" && value != "\n" && value.trim().length != 0) {
                        finalList.push(value.trim())
                    }
                });

                var targetGroup = Object.assign({}, currentTargetGroup);
                targetGroup.ContainsAnyToken = finalList;
                dispatch(updateLocalTargetGroup(targetGroup, () => {
                    setFile(files)
                }))

            }

            fileReader.addEventListener('load', eventListener);

            fileReader.readAsBinaryString(file);
        }
    }

    /**
     * Handle Downloading filter csv template
     */
    function handleDownloadOnClick() {
        window.open(`${baseUrl}/templates/filter_template.csv`, "_self")
    }

    /**
     * Handle deleting ids from ContainsAnyToken in the target group
     * @param toDelete List of CSV uploaded ids to delete
     */
    function handleDeleteCSVIds(toDelete: string[]) {
        var targetGroup = Object.assign({}, currentTargetGroup);
        targetGroup.ContainsAnyToken = targetGroup?.ContainsAnyToken?.filter((item: string) => !toDelete.includes(item))
        dispatch(updateLocalTargetGroup(targetGroup, () => {

        }))
    }

    /**
     * Check Target Group is not in an active Campaign when deleted
     */
    function retrieveData() {
        // fetch campagin data
        const data: { id: string, value: string }[] = [];
        const dataList = campaigns
        Object.values(errorCampaignIds).forEach(value => {
            const tag = dataList[value];
            data.push({ id: tag.id, value: tag.DisplayName });
        });

        return data
    }

    function deleteTargetGroupErrorAlert() {
        const items = retrieveData()
        return (
            <AlertDialog
                title={t('target-group.could-not-delete')}
                description={
                    <Box>
                        <Typography variant="body1">{t('target-group.please-remove')}</Typography>
                        <List>
                            {items.map((dataItem) => {
                                return (
                                    <ListItem
                                        key={dataItem.id}
                                        role={undefined}
                                        dense
                                    >
                                        <ListItemText id={dataItem.id} primary={dataItem.value} secondary={dataItem.id} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>

                }
                options={[
                    {
                        label: t('common.close'),
                        action: () => setDeleteTargetGroupErrorModalOpen(false)
                    },
                    {
                        label: t('target-group.go-to-campaigns'),
                        action: () => props.history.push(`/campaigns`)
                    }
                ]}
                isOpen={deleteTargetGroupErrorModalOpen}
            />
        );
    }

    function deleteTargetGroupAlert() {
        return (
            <AlertDialog
                title={t('target-group.remove-question')}
                description={t('common.remove-description')}
                options={[
                    {
                        label: t('common.cancel'),
                        action: () => setDeleteTargetGroupModalOpen(false)
                    },
                    {
                        label: t('target-group.delete'),
                        action: () => {
                            dispatch(deleteTargetGroup(targetGroupId, (success: boolean | DeleteError) => {

                                setDeleteTargetGroupModalOpen(false)
                                if (success === true) {
                                    props.history.push(`/target-groups`);
                                } else if (isError(success)) {
                                    const error = success
                                    setErrorCampaignIds(error.error_body)
                                    setFetchCampaigns(true)
                                    setDeleteTargetGroupErrorModalOpen(true)
                                }
                            }))
                        }
                    }
                ]}
                isOpen={deleteTargetGroupModalOpen}
            />
        );
    }

    /**
     * Check if incoming response data is an error or not
     * @param data Incoming response data
     */
    function isError(data: boolean | DeleteError): data is DeleteError {
        return (data as DeleteError).error_body !== undefined
    }

    function renderData() {
        if (!currentTargetGroup) {
            return <Box />
        }
        const modelType = props.type === TargetGroupType.DISTRIBUTOR ? ModelType.DISTRIBUTOR : (currentTargetGroup.ModelType || ModelType.CUSTOMERS)
        return (
            <Box className={styles.contentGrid}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <Paper>
                            <TitledCard
                                title={t('target-group.target-name')}>
                                <TextField
                                    classes={{ root: styles.textField }}
                                    label={t('target-group.target-name')}
                                    disabled={!isEditing}
                                    value={currentTargetGroup.DisplayName}
                                    onChange={(event) => {
                                        var targetGroup = Object.assign({}, currentTargetGroup);
                                        targetGroup.DisplayName = event.target.value;
                                        dispatch(updateLocalTargetGroup(targetGroup, () => { }))
                                    }}
                                    variant="outlined"
                                    margin="dense"
                                />
                            </TitledCard>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <Paper>
                            <TitledCard
                                title={t('target-group.model-type')}>
                                <DropdownSelect
                                    label={t('target-group.model-type')}
                                    disabled={props.type === TargetGroupType.DISTRIBUTOR ? true : !isEditing}
                                    handleChange={(value) => {
                                        var targetGroup = Object.assign({}, currentTargetGroup);
                                        targetGroup.ModelType = value;
                                        targetGroup.TokenField = ""
                                        targetGroup.FilterRules = []
                                        dispatch(updateLocalTargetGroup(targetGroup, () => {
                                            setFetchCodedValues(true)
                                        }))
                                    }}
                                    data={Object.values(createTargetFields())}
                                    value={modelType}
                                    compulsory
                                />
                            </TitledCard>
                        </Paper>
                    </Grid>
                    {modelType == ModelType.PRODUCTS ?
                        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                            <Paper>
                                <TitledCard
                                    title={t('target-group.limit-to-sales-org-ids')}>
                                    <TextField
                                        classes={{ root: styles.textField }}
                                        label={t('target-group.sales-organisation-ids')}
                                        disabled={!isEditing}
                                        value={currentTargetGroup.SalesOrganisationIds ? currentTargetGroup.SalesOrganisationIds.join(",") : ""}
                                        onChange={(event) => {
                                            var targetGroup = Object.assign({}, currentTargetGroup);
                                            let ids: string[] = []

                                            if (event.target.value.length > 0) {
                                                ids = event.target.value.split(",");
                                            }

                                            targetGroup.SalesOrganisationIds = ids

                                            dispatch(updateLocalTargetGroup(targetGroup, () => {

                                            }))
                                        }}
                                        variant="outlined"
                                        margin="dense"
                                        helperText={"e.g 0133, 0135"}
                                    />
                                </TitledCard>
                            </Paper>
                        </Grid> :
                        null
                    }
                    {modelType == ModelType.CUSTOMERS ?
                        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                            <Paper>
                                <TitledCard
                                    title={t('target-group.limit-to-call-types')}>
                                    <TextField
                                        classes={{ root: styles.textField }}
                                        label={t('target-group.call-type')}
                                        disabled={!isEditing}
                                        value={currentTargetGroup.CallType}
                                        onChange={(event) => {
                                            var targetGroup = Object.assign({}, currentTargetGroup);
                                            targetGroup.CallType = event.target.value;
                                            dispatch(updateLocalTargetGroup(targetGroup, () => {

                                            }))
                                        }}
                                        variant="outlined"
                                        margin="dense"
                                    />
                                </TitledCard>
                            </Paper>
                        </Grid> :
                        null
                    }
                    <VariableRuleManager
                        isEditing={isEditing}
                        modelType={modelType.toUpperCase()}
                    />
                    {
                        props?.type === TargetGroupType.VIEW ?
                            null :
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Paper>
                                    <TitledCard
                                        title={t('target-group.limit-to-specified-ids')}
                                        actionButton={
                                            <Button
                                                className={styles.buttonStyle}
                                                variant="outlined"
                                                color="secondary"
                                                onClick={handleDownloadOnClick}
                                            >
                                                {t('target-group.download-template')}
                                            </Button>
                                        }
                                    >
                                        <Box className={styles.fileDrop}>
                                            <FileDrop
                                                message={t('target-group.upload-file-of-ids')}
                                                onFilesAdded={(file: File[]) => parseFilterUpload(file)}
                                                file={file}
                                                enforceTypes={[FileType.csv]}
                                                enforceTypesMessage={t('target-group.enforce-csv')}
                                            />
                                        </Box>
                                    </TitledCard>
                                </Paper>
                            </Grid>
                    }

                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Paper>
                            <ListableCard
                                actionButton={[
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => handleDeleteCSVIds(currentTargetGroup?.ContainsAnyToken)
                                        }>
                                        {t('target-group.clear-ids')}
                                    </Button>
                                ]}
                                disabled={!isEditing || modelType === ""}
                                title={t('common.ids')}
                                emptyPlaceholder={t('common.no-ids-uploaded')}
                                data={(currentTargetGroup?.ContainsAnyToken || []).map((value: string) => { return { Name: value, id: value } })}
                                handleDelete={(ids) => handleDeleteCSVIds(ids)}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        );
    }


    return (
        <Box>
            <HeaderBlock
                title={
                    props.type === TargetGroupType.VIEW ?
                        currentTargetGroup != null ?
                            currentTargetGroup.DisplayName :
                            "" :
                        t('target-group.new-target')
                }
                right={
                    <Grid container spacing={2} alignItems="center" justify="flex-end">
                        <Grid item>
                            {
                                props?.type === TargetGroupType.VIEW ?
                                    <ButtonGroup>
                                        {isEditing ?
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => setDeleteTargetGroupModalOpen(true)}
                                            >
                                                {t('common.delete')}
                                            </Button> :
                                            null
                                        }
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => {
                                                if (isEditing) {
                                                    submitTargetGroup(false);
                                                } else {
                                                    setIsEditing(true)
                                                }
                                            }}
                                        >
                                            {isEditing ? t('common.done') : t('common.edit')}
                                        </Button>
                                    </ButtonGroup> :
                                    <ButtonGroup>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => {
                                                submitTargetGroup()
                                            }}
                                        >
                                            {t('common.submit')}
                                        </Button>
                                    </ButtonGroup>

                            }
                        </Grid>
                    </Grid>
                }
            />

            {deleteTargetGroupAlert()}
            {deleteTargetGroupErrorAlert()}

            <CustomizedSnackbars
                handleClose={() => setSnackbarOpen(false)}
                open={snackbarOpen}
                variant="error"
                message={snackbarMessage}
            />

            {
                props?.type === TargetGroupType.VIEW ?
                    null :
                    <HelpCard
                        message={t('target-group.create-segmentation')}
                    />
            }
            <PageContainer hasHelp={props?.type !== TargetGroupType.VIEW}>
                {loading ? <Loading /> : renderData()}
            </PageContainer>
        </Box >
    )
}

ViewTargetGroupsPage.propTypes = {

}

export default ViewTargetGroupsPage

